import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert,
  FormControlLabel, Checkbox, Divider, TextField
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import api from '../axiosConfig';

/**
 * PricelistGenerator
 * Fetches tier prices for all services and opens a printable HTML page in a new tab.
 * Salon name is loaded from business settings (company_info) on dialog open.
 * User can override the name before generating.
 */
const PricelistGenerator = ({ services, categories, open, onClose }) => {
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [showUnpriced, setShowUnpriced] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [salonName, setSalonName]       = useState('');
  const [nameLoading, setNameLoading]   = useState(false);

  // Load salon name from business settings whenever dialog opens
  useEffect(() => {
    if (!open) return;
    setNameLoading(true);
    api.get('/calculator/settings/business')
      .then(res => setSalonName(res.data?.salon_name || ''))
      .catch(() => setSalonName(''))
      .finally(() => setNameLoading(false));
  }, [open]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const tiersRes = await api.get('/calculator/tiers');
      const tiers = tiersRes.data || [];

      const tierPriceResults = await Promise.allSettled(
        services.map(s =>
          api.get(`/calculator/services/${s.id}/tier-prices`)
            .then(r => ({ serviceId: s.id, prices: r.data || [] }))
        )
      );

      const priceMap = {};
      tierPriceResults.forEach(result => {
        if (result.status === 'fulfilled') {
          const { serviceId, prices } = result.value;
          priceMap[serviceId] = {};
          prices.forEach(p => { priceMap[serviceId][p.tier_id] = p.price; });
        }
      });

      const html = buildPricelistHTML({
        services, categories, tiers, priceMap,
        salonName: salonName.trim() || 'Our Services',
        showUnpriced, groupByCategory
      });

      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      onClose();
    } catch (err) {
      console.error('Pricelist generation error:', err);
      setError('Failed to load pricing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Generate Pricelist</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates a printable pricelist showing your tier prices per service.
          Opens in a new tab — use Ctrl+P / Cmd+P to print or save as PDF.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TextField
          label="Salon name on pricelist"
          value={salonName}
          onChange={e => setSalonName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          disabled={nameLoading}
          placeholder={nameLoading ? 'Loading…' : 'Our Services'}
          helperText="Pre-filled from your company settings. Edit here to use a different heading for this pricelist only."
        />
        <FormControlLabel
          control={<Checkbox checked={groupByCategory} onChange={e => setGroupByCategory(e.target.checked)} />}
          label="Group services by category"
        />
        <br />
        <FormControlLabel
          control={<Checkbox checked={showUnpriced} onChange={e => setShowUnpriced(e.target.checked)} />}
          label="Include services with no prices set"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={loading || nameLoading || services.length === 0}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        >
          {loading ? 'Building…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// HTML builder — pure function, no React
// ============================================================================

function buildPricelistHTML({ services, categories, tiers, priceMap, salonName, showUnpriced, groupByCategory }) {
  const fmt = (v) => v != null && v !== '' ? `£${parseFloat(v).toFixed(2)}` : '—';

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : 'Uncategorised';
  };

  const formatDuration = (mins) => {
    if (!mins) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}hr`;
    return `${h}hr ${m}min`;
  };

  // Filter services that have at least one tier price set (unless showUnpriced)
  const visibleServices = services.filter(s => {
    if (showUnpriced) return true;
    const prices = priceMap[s.id] || {};
    return Object.values(prices).some(p => p != null && p !== '' && parseFloat(p) > 0);
  });

  // Group
  let sections = [];
  if (groupByCategory) {
    const grouped = {};
    visibleServices.forEach(s => {
      const key = s.category_id || '__none__';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(s);
    });
    // Sort categories by name, uncategorised last
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === '__none__') return 1;
      if (b === '__none__') return -1;
      return getCategoryName(parseInt(a)).localeCompare(getCategoryName(parseInt(b)));
    });
    sections = sortedKeys.map(key => ({
      label: key === '__none__' ? 'Uncategorised' : getCategoryName(parseInt(key)),
      services: grouped[key].sort((a, b) => a.name.localeCompare(b.name))
    }));
  } else {
    sections = [{
      label: null,
      services: [...visibleServices].sort((a, b) => a.name.localeCompare(b.name))
    }];
  }

  // Tier columns — only include tiers that have at least one price across visible services
  const activeTierIds = new Set();
  visibleServices.forEach(s => {
    const prices = priceMap[s.id] || {};
    tiers.forEach(t => {
      if (prices[t.id] != null && parseFloat(prices[t.id]) > 0) {
        activeTierIds.add(t.id);
      }
    });
  });
  const activeTiers = tiers.filter(t => activeTierIds.has(t.id));

  // Single-tier mode: just show one price column with no header
  const singleTier = activeTiers.length <= 1;

  const tierHeaders = singleTier
    ? `<th class="price-col">Price</th>`
    : activeTiers.map(t => `<th class="price-col">${t.name}</th>`).join('');

  const renderRows = (svcList) => svcList.map(s => {
    const prices = priceMap[s.id] || {};
    const durationStr = formatDuration(s.duration);
    const priceCells = singleTier
      ? (() => {
          const t = activeTiers[0];
          const val = t ? prices[t.id] : null;
          return `<td class="price-col">${fmt(val)}</td>`;
        })()
      : activeTiers.map(t => `<td class="price-col">${fmt(prices[t.id])}</td>`).join('');

    return `
      <tr>
        <td class="service-name">
          ${s.name}
          ${durationStr ? `<span class="duration">${durationStr}</span>` : ''}
        </td>
        ${priceCells}
      </tr>`;
  }).join('');

  const tableBody = sections.map(section => {
    if (section.services.length === 0) return '';
    const categoryRow = section.label
      ? `<tr class="category-row"><td colspan="${1 + (singleTier ? 1 : activeTiers.length)}">${section.label}</td></tr>`
      : '';
    return categoryRow + renderRows(section.services);
  }).join('');

  const printDate = new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  });

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${salonName} — Pricelist</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=Inter:wght@300;400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --brand:   #667eea;
      --dark:    #1a1a2e;
      --mid:     #4a4a6a;
      --light:   #f7f7fb;
      --rule:    #e2e2ee;
      --accent:  #764ba2;
    }

    body {
      font-family: 'Inter', sans-serif;
      font-size: 13px;
      color: var(--dark);
      background: #fff;
      padding: 0;
    }

    /* ── Cover strip ── */
    .cover {
      background: linear-gradient(135deg, var(--brand) 0%, var(--accent) 100%);
      color: #fff;
      padding: 36px 48px 28px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
    }
    .cover-name {
      font-family: 'Playfair Display', serif;
      font-size: 28px;
      font-weight: 600;
      letter-spacing: -0.3px;
      line-height: 1.1;
    }
    .cover-sub {
      font-size: 12px;
      opacity: 0.8;
      margin-top: 4px;
      letter-spacing: 0.8px;
      text-transform: uppercase;
    }
    .cover-date {
      font-size: 11px;
      opacity: 0.7;
      text-align: right;
      line-height: 1.6;
    }

    /* ── Table ── */
    .wrapper { padding: 32px 48px 48px; }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    thead th {
      font-family: 'Inter', sans-serif;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.9px;
      text-transform: uppercase;
      color: var(--mid);
      padding: 8px 0;
      border-bottom: 1.5px solid var(--dark);
      text-align: left;
    }
    thead th.price-col {
      text-align: right;
      min-width: 80px;
    }

    /* Category rows */
    tr.category-row td {
      font-family: 'Playfair Display', serif;
      font-size: 15px;
      font-weight: 600;
      color: var(--accent);
      padding: 20px 0 6px;
      border-bottom: 1px solid var(--rule);
      letter-spacing: -0.1px;
    }

    /* Service rows */
    tbody tr:not(.category-row) {
      border-bottom: 1px solid var(--rule);
    }
    tbody tr:not(.category-row):last-child {
      border-bottom: none;
    }
    tbody tr:not(.category-row) td {
      padding: 9px 0;
      vertical-align: middle;
    }
    tbody tr:not(.category-row):hover td {
      background: var(--light);
    }

    .service-name {
      font-weight: 400;
      color: var(--dark);
      padding-right: 16px !important;
    }
    .duration {
      display: inline-block;
      margin-left: 8px;
      font-size: 11px;
      color: var(--mid);
      font-weight: 300;
    }

    td.price-col {
      text-align: right;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
      min-width: 80px;
      color: var(--dark);
    }

    /* ── Footer ── */
    .footer {
      padding: 0 48px 24px;
      font-size: 10px;
      color: #aaa;
      display: flex;
      justify-content: space-between;
    }

    /* ── Print ── */
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
      .cover { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      tr.category-row td { color: var(--accent) !important; }
      @page { margin: 0; size: A4; }
    }
  </style>
</head>
<body>

  <div class="cover">
    <div>
      <div class="cover-name">${salonName}</div>
      <div class="cover-sub">Price List</div>
    </div>
    <div class="cover-date">
      Prices from<br>${printDate}
    </div>
  </div>

  <div class="no-print" style="background:#f0f0f0;padding:10px 48px;font-size:12px;color:#555;display:flex;justify-content:space-between;align-items:center;">
    <span>Press <strong>Ctrl+P</strong> (or <strong>Cmd+P</strong> on Mac) to print or save as PDF.</span>
    <button onclick="window.print()" style="background:linear-gradient(135deg,#667eea,#764ba2);color:white;border:none;padding:6px 16px;border-radius:4px;cursor:pointer;font-size:12px;">
      Print / Save PDF
    </button>
  </div>

  <div class="wrapper">
    <table>
      <thead>
        <tr>
          <th>Service</th>
          ${tierHeaders}
        </tr>
      </thead>
      <tbody>
        ${tableBody || '<tr><td colspan="10" style="padding:24px 0;color:#aaa;">No priced services found.</td></tr>'}
      </tbody>
    </table>
  </div>

  <div class="footer">
    <span>All prices are subject to change. Please ask at reception for the latest pricing.</span>
    <span>Generated ${printDate}</span>
  </div>

</body>
</html>`;
}

export default PricelistGenerator;